import { b64toBlob } from '~core/b64toBlob';
import { cleanBase64 } from '~src/utils';

export const BACKGROUND_FUSION_MASK_SHAPES = {
  PORTRAIT: 'portrait',
  FRAMED_SUBJECT: 'framed_subject',
};

export const assertValidCanvasDataUrl = (dataUrl, dimensions = {}) => {
  if (!dataUrl || dataUrl === 'data:,') {
    throw new Error(
      `Unable to export background fusion image for ${dimensions.width}x${dimensions.height}`
    );
  }

  return dataUrl;
};

const loadCrossOriginImage = (imageUrl, errorMessage) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(errorMessage));
    image.src = imageUrl;
  });

export const imageUrlToExactDataUrl = (
  imageUrl,
  { width, height, format = 'image/png' }
) =>
  loadCrossOriginImage(imageUrl, 'Unable to load generated image').then(
    (image) => {
      const canvasWidth = Math.round(width || 0);
      const canvasHeight = Math.round(height || 0);

      if (!canvasWidth || !canvasHeight) {
        throw new Error('Target dimensions are required');
      }

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvasWidth, canvasHeight);

      return assertValidCanvasDataUrl(canvas.toDataURL(format), {
        width: canvasWidth,
        height: canvasHeight,
      });
    }
  );

const getMaskFeather = ({ width, height, person }) => {
  const shortestCanvasEdge = Math.min(width, height);
  const longestPersonEdge = Math.max(person.width, person.height);

  return Math.round(
    Math.min(
      Math.max(shortestCanvasEdge * 0.015, longestPersonEdge * 0.06, 32),
      96
    )
  );
};

const getMaskMargin = ({ width, height, person }) => {
  const shortestCanvasEdge = Math.min(width, height);
  const longestPersonEdge = Math.max(person.width, person.height);

  return Math.round(
    Math.min(
      Math.max(shortestCanvasEdge * 0.008, longestPersonEdge * 0.04, 24),
      72
    )
  );
};

const getFramedSubjectMaskMargin = ({ width, height, person }) => {
  const shortestCanvasEdge = Math.min(width, height);
  const longestPersonEdge = Math.max(person.width, person.height);

  return Math.round(
    Math.min(
      Math.max(shortestCanvasEdge * 0.015, longestPersonEdge * 0.08, 48),
      128
    )
  );
};

const fillPortraitMask = ({ context, person, margin }) => {
  const centerX = person.left + person.width / 2;
  const centerY = person.top + person.height / 2;
  const radiusX = person.width * 0.58 + margin;
  const radiusY = person.height * 0.58 + margin;

  context.beginPath();
  context.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
  context.fill();
};

const fillFramedSubjectMask = ({ context, person, margin }) => {
  context.beginPath();
  context.rect(
    person.left - margin,
    person.top - margin,
    person.width + margin * 2,
    person.height + margin * 2
  );
  context.fill();
};

export const createBackgroundFusionMaskDataUrl = ({
  width,
  height,
  person,
  shape = BACKGROUND_FUSION_MASK_SHAPES.PORTRAIT,
}) => {
  const canvasWidth = Math.round(width || 0);
  const canvasHeight = Math.round(height || 0);

  if (!canvasWidth || !canvasHeight) {
    throw new Error(
      'Target dimensions are required for background fusion mask'
    );
  }

  if (!person || person.width <= 0 || person.height <= 0) {
    throw new Error('Person placement is required for background fusion mask');
  }

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = canvasWidth;
  maskCanvas.height = canvasHeight;

  const context = maskCanvas.getContext('2d');
  const feather = getMaskFeather({
    width: canvasWidth,
    height: canvasHeight,
    person,
  });
  const isFramedSubject =
    shape === BACKGROUND_FUSION_MASK_SHAPES.FRAMED_SUBJECT;
  const margin = isFramedSubject
    ? getFramedSubjectMaskMargin({
        width: canvasWidth,
        height: canvasHeight,
        person,
      })
    : getMaskMargin({
        width: canvasWidth,
        height: canvasHeight,
        person,
      });
  const fillMask = isFramedSubject ? fillFramedSubjectMask : fillPortraitMask;

  context.fillStyle = 'rgba(0, 0, 0, 1)';
  context.fillRect(0, 0, canvasWidth, canvasHeight);

  context.save();
  context.shadowColor = 'rgba(255, 255, 255, 1)';
  context.shadowBlur = feather;
  context.fillStyle = 'rgba(255, 255, 255, 1)';
  fillMask({ context, person, margin });
  context.shadowBlur = 0;
  fillMask({ context, person, margin });
  context.restore();

  return assertValidCanvasDataUrl(maskCanvas.toDataURL('image/png'), {
    width: canvasWidth,
    height: canvasHeight,
  });
};

export const uploadDataUrlToUploader = async ({
  dataUrl,
  uploader,
  customAttachmentProps,
  name,
}) => {
  if (!uploader) {
    throw new Error('Missing uploader for background fusion image');
  }

  const imageBase64 = cleanBase64(dataUrl);
  const blob = b64toBlob(imageBase64.base64, imageBase64.contentType);

  const file = {
    ...(name ? { name } : {}),
    type: blob.type,
    data: blob,
  };

  if (customAttachmentProps) {
    file.meta = { customAttachmentProps };
  }

  uploader.addFile(file);

  const responseUpload = await uploader.upload();
  if (!responseUpload.successful || responseUpload.successful.length === 0) {
    uploader.cancelAll();
    throw new Error('Upload failed');
  }

  uploader.cancelAll();

  return `${process.env.EDGE_RESIZER}${
    new URL(responseUpload.successful[0].uploadURL).pathname
  }`;
};
